import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ApiError } from 'src/api/axiosClient/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { buildCreateRequestData, buildFormData, buildUpdateRequestData } from '../helpers';
import { useGroupService } from '../services';
import { Group, GroupFormData, GroupType } from '../types';

export type OpenGroupFormOptions = {
  initialName?: string;
  initialType?: Extract<GroupType, 'custom' | 'dynamic'>;
};

export interface GroupFormDialogController {
  isOpen: boolean;
  initFormData: GroupFormData | null;
  error: ApiError | null;
  open: (group?: Group, options?: OpenGroupFormOptions) => void;
  close: () => void;
  syncRules: (rules: GroupFormData['rules']) => void;
  submit: (
    full: GroupFormData,
    dirty: Partial<GroupFormData>,
    nextAction?: (group?: Group) => unknown | Promise<unknown>,
  ) => Promise<void>;
}

export const useGroupFormDialogController = (): GroupFormDialogController => {
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;
  const [initFormData, setInitFormData] = useState<GroupFormData | null>(null);
  const latestRulesRef = useRef<GroupFormData['rules']>([]);

  const { createGroup, updateGroup, error } = useGroupService();

  const open = useCallback((group?: Group, options?: OpenGroupFormOptions) => {
    const formData = {
      ...buildFormData(group),
      ...(!group && options?.initialName ? { name: options.initialName } : {}),
      ...(!group && options?.initialType ? { type: options.initialType } : {}),
    };
    setInitFormData(formData);
    latestRulesRef.current = formData.rules || [];
  }, []);

  const close = useCallback(() => {
    setInitFormData(null);
  }, []);

  const syncRules = useCallback((rules: GroupFormData['rules']) => {
    latestRulesRef.current = rules;
  }, []);

  const submit = useCallback(
    async (
      full: GroupFormData,
      dirty: Partial<GroupFormData>,
      nextAction?: (group?: Group) => Promise<unknown>,
    ) => {
      const { id: groupId } = full;

      try {
        if (groupId) {
          await updateGroup(
            currentCompanyId,
            groupId,
            buildUpdateRequestData({ ...full, rules: latestRulesRef.current }),
          );
          close();
          await nextAction?.();
        } else {
          const response = await createGroup(currentCompanyId, buildCreateRequestData(full));
          close();
          await nextAction?.(response.data);
        }
      } catch (e) {
        console.log(e);
      }
    },
    [],
  );

  return {
    isOpen: !!initFormData,
    initFormData,
    error,
    open,
    close,
    syncRules,
    submit,
  };
};
